import * as React from 'react';
import { cn } from '../../lib/utils';

export interface GaugeReading {
  id: string;
  /** Eyebrow label (e.g. "Availability"). */
  label: string;
  /** 0..100 percentage value. */
  value: number;
  /** Threshold for "good" (>= = green). Defaults to 85. */
  goodAt?: number;
  /** Threshold for "warning" (>= = amber). Defaults to 60. */
  warnAt?: number;
  /** Optional sub-label (e.g. "Last 24h"). */
  sub?: string;
  /** Live indicator — pulses Phosphor halo while live. */
  live?: boolean;
}

export interface GaugeBankProps {
  /** 1..6 gauges; typically OEE / A / P / Q. */
  readings: GaugeReading[];
  /** Tile size in px. */
  size?: number;
  className?: string;
}

/**
 * Tyndo GaugeBank — composable row of half-arc gauges.
 *
 * Use on Production / Quality / SCADA dashboards. Pure SVG, no Recharts.
 * Color tone derived from goodAt / warnAt thresholds. Designed to read at
 * 6ft on a control-room screen and at desk distance on a laptop.
 */
export function GaugeBank({ readings, size = 110, className }: GaugeBankProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-stretch gap-3 rounded-lg border border-border bg-surface-raised p-4',
        className
      )}
    >
      {readings.map((r) => (
        <GaugeTile key={r.id} reading={r} size={size} />
      ))}
    </div>
  );
}

function GaugeTile({ reading, size }: { reading: GaugeReading; size: number }) {
  const { value, label, sub, goodAt = 85, warnAt = 60, live } = reading;
  const v = Math.max(0, Math.min(100, value));

  const tone =
    v >= goodAt
      ? 'hsl(var(--state-running))'
      : v >= warnAt
        ? 'hsl(var(--alarm-minor))'
        : 'hsl(var(--alarm-critical))';

  const r = (size - 14) / 2;
  const c = size / 2;
  const halfCircumference = Math.PI * r;
  const dash = (v / 100) * halfCircumference;

  return (
    <div
      className="group relative flex flex-1 min-w-[120px] flex-col items-center gap-1 rounded-md border border-border/60 bg-surface-base p-3"
    >
      <div
        className="relative flex items-end justify-center"
        style={{ width: size, height: size / 2 + 6 }}
      >
        <svg
          width={size}
          height={size / 2 + 6}
          viewBox={`0 0 ${size} ${size / 2 + 6}`}
          aria-label={`${label} ${v}%`}
        >
          <path
            d={`M ${c - r},${c + 3} A ${r} ${r} 0 0 1 ${c + r},${c + 3}`}
            fill="none"
            stroke="hsl(var(--surface-sunken))"
            strokeWidth={10}
            strokeLinecap="round"
          />
          <path
            d={`M ${c - r},${c + 3} A ${r} ${r} 0 0 1 ${c + r},${c + 3}`}
            fill="none"
            stroke={tone}
            strokeWidth={10}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${halfCircumference}`}
            style={{ transition: 'stroke-dasharray 400ms cubic-bezier(0.16, 1, 0.3, 1)' }}
          />
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-end pb-0.5">
          <span
            className="t-tabular tabular-nums text-2xl font-semibold leading-none"
            style={{ color: tone }}
          >
            {Math.round(v)}
            <span className="ml-0.5 text-[10px] text-muted-foreground font-normal">%</span>
          </span>
        </div>
        {live && (
          <span
            aria-hidden
            className="absolute right-0 top-0 h-2 w-2 tyndo-pulse rounded-full"
            style={{ backgroundColor: 'hsl(var(--accent-live))' }}
          />
        )}
      </div>
      <p className="t-eyebrow mt-1">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}
